import React, { useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { setStagingSession } from "../../redux/slices/adminEnvSlice";
import { adminSurfaceSx } from "./adminStyles";

// The admin email — staging's roleForEmail bakes role:'admin' at login for this
// one address, so logging into staging as anyone else yields a role:'user' token
// that 403s on the admin routes. Pre-filled (but editable) to make that explicit.
const ADMIN_EMAIL = "williamjonescodes@gmail.com";

// One-time staging login for the admin console. Staging's JWT_SECRET differs
// from prod's, so admin data fetches in staging mode need a STAGING-signed admin
// token. This dialog mints one via the existing login path routed at staging
// (POST /api/account/login?env=staging), then stores it in adminEnvSlice
// (redux + localStorage) so every subsequent staging admin fetch reuses it.
//
// Auth model: this token is ONLY a data-source credential for staging fetches.
// It never gates the /admin route — AdminRoute still requires the prod admin.
export default function StagingLoginDialog({ open, onClose }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Route the login at staging (env is routing-only, stripped before the
      // proxy forwards to the worker). Reads the JWT the same way authSlice does.
      const response = await axios.post(
        "/api/account/login",
        { email: email.trim(), password },
        { params: { env: "staging" } }
      );
      const token = response.data?.access_token;
      if (!token) {
        setError("Staging login returned no token — try again.");
        setSubmitting(false);
        return;
      }
      dispatch(setStagingSession({ token, email: email.trim() }));
      setPassword("");
      setSubmitting(false);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError(
          "Staging login failed — is this the admin email with a staging password?"
        );
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Staging login failed — try again."
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: adminSurfaceSx(theme) } }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>Log in to staging</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            Staging runs its own auth (a different JWT secret), so admin data in
            staging mode needs a staging-signed token. Log in once as the admin
            email; the token is held for every staging admin fetch.
          </Typography>
          {error && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            slotProps={{ htmlInput: { "data-testid": "staging-login-email" } }}
          />
          <TextField
            fullWidth
            label="Staging password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            slotProps={{
              htmlInput: { "data-testid": "staging-login-password" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !password}
            data-testid="staging-login-submit"
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            Log in
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
